/**
 * A LAZY IMPORT THAT SURVIVES A DEPLOY.
 *
 * ── ⚠️ THE FAILURE THIS EXISTS FOR ────────────────────────────────────────────────
 * "Failed to fetch dynamically imported module: …/assets/twilio-DDAaRbGA.js".
 *
 * Vite names every chunk by its CONTENT, and a build replaces the whole `assets/` folder.
 * A tab opened before a deploy is still running the old `index.html`, which knows the old
 * names — so the moment it reaches for a chunk it has not loaded yet, it asks for a file
 * that no longer exists and gets a 404. The page looks fine until then, because everything
 * it had already downloaded still works.
 *
 * It bites hardest on exactly this kind of import: the call SDK is deliberately not in the
 * main bundle, so the first fetch happens when a passenger presses Call — potentially days
 * after the tab was opened, and at the least forgiving moment there is.
 *
 * ── ⚠️ RELOADING IS THE ONLY REAL CURE ───────────────────────────────────────────
 * The missing file is missing; there is nothing to retry. What is stale is the HTML, and a
 * reload fetches the current one, whose names match what is on disk.
 *
 * ⚠️ ONCE, AND THE GUARD IS NOT OPTIONAL. If the chunk is absent for any other reason — a
 * bad deploy, a CDN serving a half-uploaded folder, an offline device — reloading would
 * loop forever on a screen the user cannot escape. The flag is cleared on the next
 * successful import, so the NEXT deploy is allowed its own reload.
 *
 * ⚠️ AND ONLY FOR THIS ERROR. Anything else — the module threw while evaluating, the SDK
 * rejected a token — is a real failure and is rethrown for the caller to show.
 */

const RELOADED_KEY = 'hopn:stale-chunk-reloaded'

/** The browsers word it three different ways; all three mean the file was not served. */
const STALE_CHUNK = /failed to fetch dynamically imported module|error loading dynamically imported module|importing a module script failed/i

export function isStaleChunkError(error) {
    return STALE_CHUNK.test(String(error?.message || error || ''))
}

/** Safe in a private window, where storage access can throw rather than return null. */
const readFlag = () => { try { return sessionStorage.getItem(RELOADED_KEY) } catch { return null } }
const writeFlag = (value) => {
    try {
        if (value === null) sessionStorage.removeItem(RELOADED_KEY)
        else sessionStorage.setItem(RELOADED_KEY, value)
    } catch { /* storage unavailable — the guard degrades to "never reload", which is safe */ }
}

/**
 * @param {Function} load the `() => import('…')` to run
 * @returns whatever the module resolves to. On a stale chunk it reloads the page instead,
 *   and the returned promise never settles — the document is on its way out.
 */
export async function importOrReload(load) {
    try {
        const module = await load()
        // We got a chunk, so the names match again: let a future deploy reload once too.
        if (readFlag()) writeFlag(null)
        return module
    } catch (error) {
        if (!isStaleChunkError(error) || readFlag()) throw error
        writeFlag('1')
        window.location.reload()
        return new Promise(() => {})
    }
}

export default importOrReload
